export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  litellm_connection_status: 'connected' | 'failed' | null;
  litellm_connection_tested_at: string | null;
  created_at: string;
  updated_at: string;
  organization: string | null;
  organization_name?: string | null;
  // DigiPART assignment (admin view)
  digipart_username: string | null;
  digipart_connection_status: 'not_assigned' | 'assigned' | 'connected' | 'session_expired' | 'failed' | null;
  digipart_assigned_by_email: string | null;
  digipart_assigned_at: string | null;
}

export interface Organization {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  user_count?: number;
}

export interface OrganizationCreate {
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  full_name: string;
}

export interface AuthResponse {
  access: string;
  refresh: string;
}

export interface FileUpload {
  id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  row_count: number;
  column_count: number;
  columns: string[];
  uploaded_at: string;
  preview_data?: {
    columns: string[];
    rows: Record<string, string | number | boolean | null>[];
  };
}

export interface ProcessingJob {
  id: number | string;
  filename: string;
  job_name?: string;
  project_name?: string;
  organization_name?: string | null;
  prompt_text?: string;
  file: number;
  file_upload?: {
    original_filename?: string;
    filename?: string;
  };
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'suspended' | 'awaiting_validation' | 'completed_with_errors';
  progress: number;
  status_message?: string;  // Current processing stage message (e.g., "Sending API Request")
  current_chunk?: number;
  total_chunks?: number;
  processed_rows?: number;
  total_rows?: number;
  cost_usd?: number | string; // Decimal fields come as strings from Django
  cost_sgd?: number | string; // Decimal fields come as strings from Django
  total_tokens?: number;
  successful_chunks?: number;
  failed_chunks?: number;
  error_message?: string;
  error_details?: {
    error_type?: string;
    message?: string;
    traceback?: string;
    chunk_number?: number;
    row_number?: number;
    column?: string;
    details?: Record<string, unknown>;
  };
  result_file_path?: string;
  results_file_path?: string;
  result_filename?: string;
  output_filename?: string;
  model_name?: string;
  pipeline_type?: string;
  chunk_size?: number;
  temperature?: number;
  max_concurrent?: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  updated_at?: string;
  config?: {
    model_name?: string;
    chunk_size?: number;
    temperature?: number;
    max_concurrent?: number;
    output_multiplier?: number;
    join_column?: string;
    pipeline_type?: string;
    system_instructions?: string;
    use_vertex_ai?: boolean;
    [key: string]: unknown;
  };
  celery_task_id?: string;
  is_replay?: boolean;
  checkpoint_data?: {
    last_completed_batch?: number;
    total_batches?: number;
    chunks_completed?: number;
    total_chunks?: number;
    total_tokens?: number;
    total_cost_usd?: number;
    processed_rows?: number;
    total_rows?: number;
    saved_at?: string;
  };
  completed_chunks?: number[];
  partial_results_file?: string;
  // NEW: Checkpoint information from ProcessingCheckpoint model
  checkpoint_info?: {
    exists: boolean;
    last_completed_batch?: number;
    last_completed_chunk?: number;
    progress_percentage?: number;
    cost_spent_usd?: string;
    cost_spent_sgd?: string;
    updated_at?: string;
  };
  can_resume?: boolean;
  // True when the job may be abandoned. Wider than "still running": a suspended
  // job is cancellable even when it cannot be resumed, so it is never a dead end
  // (PDR-032). Derived server-side from job_states.CANCELLABLE_STATUSES.
  can_cancel?: boolean;
  // Machine-readable suspension reason (PIPELINE_WORKER_TIMEOUT, QUOTA_EXHAUSTED, WORKER_LOST, DIGIPART_SESSION_EXPIRED)
  suspension_code?: string | null;
  // True when a partial-results CSV has been saved and at least one row was processed
  has_partial_results?: boolean;
  // Part category pipeline: catalogue being processed
  catalogue_id?: string | null;
  // Validation queue data (stored in processing_details for part_category pipeline)
  processing_details?: {
    validation_queue?: ValidationQueueEntry[];
    taxonomy_snapshot?: TaxonomyEntry[];
    total_parts?: number;
    unreviewed_count?: number;
    edited_count?: number;
    excluded_count?: number;
    synced_count?: number;
    write_errors?: WritebackError[];
    validation_submitted_at?: string | null;
    validation_submitted_by?: string | null;
    stats?: Record<string, number>;
  };
}

export interface TaxonomyEntry {
  id: string;
  name: string;
}

export interface ValidationQueueEntry {
  part_id: string;
  part_number: string;
  part_name: string;
  short_description: string;
  long_description?: string;
  current_category_id: string | null;
  current_category_name: string | null;
  ai_category_1_id: string | null;
  ai_category_1_name: string;
  ai_category_1_unresolved?: boolean;
  ai_category_1_fuzzy_id?: string | null;
  ai_category_1_fuzzy_name?: string | null;
  ai_category_1_fuzzy_score?: number;
  ai_category_2_id: string | null;
  ai_category_2_name: string | null;
  ai_category_2_unresolved?: boolean;
  ai_category_3: string | null;
  selected_category_id: string | null;
  selected_category_name: string | null;
  /**
   * Server-assigned review state, used for the Status column and for
   * `?sort=review_status_*`. Derived per request from selected_category_id,
   * never stored. Optional because a row held only in local edit state has not
   * been classified by the server yet.
   * Decision: PDR-054 — see docs/decisions/PDR-054-validation-review-status-is-ranked-by-urgency.md
   */
  review_status?: ReviewStatus;
}

/** Four mutually exclusive states, ranked by urgency for sorting (PDR-054). */
export type ReviewStatus = 'flagged' | 'unreviewed' | 'edited' | 'excluded';

export interface WritebackError {
  part_id: string;
  part_number: string;
  part_name: string;
  error_code: string | number;
  detail: string;
}

/**
 * Extended processing status for Dimension pipeline
 * Includes phase tracking, statistics, events, and alerts
 */
export interface DimensionProcessingStatus extends ProcessingJob {
  processing_details?: {
    // Phase tracking
    current_phase?: 'init' | 'loading' | 'pre_dedup' | 'ai_validate' | 'ai_create' | 
                    'post_dedup' | 'compliance' | 'final_dedup' | 'sync' | 'complete';
    phase_name?: string;
    phase_progress?: number;  // 0-100 for current phase
    
    // Current part being processed
    current_part?: {
      part_id: string;
      part_number: string;
      shape: string;
      existing_dimensions_count: number;
    };
    
    // Cumulative statistics
    stats?: {
      dimensions_extracted: number;
      dimensions_created: number;
      dimensions_updated: number;
      duplicates_removed_pre_ai: number;
      conflicts_resolved_post_ai: number;
      duplicates_skipped_final: number;
      api_calls_made: number;
      cost_usd: number;
      // Token & cost totals populated by accumulate_token_stats via Redis
      total_input_tokens: number;
      total_output_tokens: number;
      total_cost_usd: number;
      total_cost_sgd: number;
    };
    
    // Recent activity log (last 5 events)
    recent_events?: Array<{
      timestamp: string;
      type: 'info' | 'warning' | 'success' | 'error';
      message: string;
      details?: string;  // Expandable details
    }>;
    
    // Active alerts. `type` is open-ended: the backend add_alert contract also
    // emits `error`, `session_expired`, and arbitrary suspension codes, so this
    // is a string (AlertsBanner falls back to a default style for unknown types).
    alerts?: Array<{
      type: string;
      message: string;
      details?: string;
    }>;
  };
}

/**
 * Type guard to check if a job has dimension processing details
 */
export function isDimensionProcessingStatus(
  job: ProcessingJob
): job is DimensionProcessingStatus {
  return (job.pipeline_type === 'dimensions' || job.pipeline_type === 'dimensions_verification') && 
         ('processing_details' in job && job.processing_details !== undefined);
}

// ── T-68e — Part Category pipeline types ─────────────────────────────────

export type PartCategoryStage =
  | 'taxonomy_fetch'
  | 'parts_fetch'
  | 'ai_categorisation'
  | 'awaiting_validation'
  | 'write_back'
  | 'complete';

// Numeric fields are optional at runtime: a resumed/partway job — or one whose
// cost blob predates a field — can omit any of them. Consumers must coalesce
// (`?? 0`) rather than call `.toFixed()`/`.toLocaleString()` on a bare value,
// which crashes the render. See PartCategoryCostBreakdown / DeduplicationStatsPanel.
export interface PartCategoryChunkCost {
  chunk_index: number;
  parts_in_chunk?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  cost_usd?: number;
  cost_sgd?: number;
}

export interface PartCategoryCost {
  total_prompt_tokens?: number;
  total_completion_tokens?: number;
  total_cost_usd?: number;
  total_cost_sgd?: number;
  chunk_costs?: PartCategoryChunkCost[];
}

export interface PartCategoryEstimatedCost {
  chunk_size_planned?: number;
  total_chunks?: number;
  tokens_for_full_chunk?: number;
  tokens_for_last_chunk?: number;
  total_input_tokens?: number;
  total_output_tokens?: number;
  usd?: number;
  sgd?: number;
  model?: string;
  output_multiplier?: number;
  pricing_note?: string;
  chunk_discovery?: {
    initial_estimate: number;
    confirmed_chunk_size: number;
    reductions_needed: number;
    iterations: Array<{
      iteration: number;
      chunk_size_tested: number;
      input_tokens: number;
      expected_output_tokens: number;
      safe_budget: number;
      result: 'fits' | 'too_large';
    }>;
  };
}

export interface PartCategoryPreflightData {
  total_parts: number;
  uncategorised_parts: number;
  estimated_cost?: PartCategoryEstimatedCost;
}

/** Processing details shape for part_category pipeline jobs */
export interface PartCategoryProcessingDetails {
  current_stage?: PartCategoryStage;
  stage_name?: string;
  stage_progress?: number;       // 0–100
  chunk_size_used?: number;
  chunks_total?: number;
  last_processed_chunk?: number;
  total_parts?: number;
  uncategorised_parts?: number;
  parts_written?: number;
  parts_skipped?: number;
  parse_warnings?: string[];
  memory_log?: Array<{ label: string; rss_mb: number }>;
  cost?: PartCategoryCost;
  // Present after finalise_gate
  validation_queue?: ValidationQueueEntry[];
  taxonomy_snapshot?: TaxonomyEntry[];
  unreviewed_count?: number;
  edited_count?: number;
  excluded_count?: number;
  synced_count?: number;
  write_errors?: WritebackError[];
  validation_submitted_at?: string | null;
  validation_submitted_by?: string | null;
  /** Mirrors ProcessingJob.processing_details.stats — kept explicit so the
   *  [key: string]: unknown index signature does not widen stats to unknown */
  stats?: Record<string, number>;
  [key: string]: unknown;
}

export interface PartCategoryJob extends ProcessingJob {
  /** Discriminant: prevents ProcessingJob from being structurally assignable to PartCategoryJob */
  pipeline_type: 'part_category';
  processing_details?: PartCategoryProcessingDetails;
}

/** Type guard — true when job is a part_category pipeline with processing_details */
export function isPartCategoryJob(job: ProcessingJob): job is PartCategoryJob {
  return job.pipeline_type === 'part_category' &&
    'processing_details' in job &&
    job.processing_details !== undefined;
}

export interface StartProcessingRequest {
  file_id: string | number;  // String UUID or number ID
  model_name?: string;
  chunk_size?: number;
  max_concurrent?: number;
  system_instructions?: string;
  use_vertex_ai?: boolean;
  pipeline_type?: string;
  prompt_text?: string;
  project_name?: string;
  job_name?: string;
  join_column?: string;
  output_multiplier?: number;
  temperature?: number;
  prompt_template_id?: string;
  config?: Record<string, any>;  // Generic config for pipeline-specific data
}

export interface ApiKeysUpdate {
  litellm_api_key?: string;
}

export interface ApiKeyStatus {
  has_litellm_key: boolean;
  litellm_connection_status: 'connected' | 'failed' | null;
  litellm_connection_tested_at: string | null;
  litellm_test_message?: string | null;
}

export interface DigiPARTConnectionUpdate {
  endpoint?: string;
  api_key?: string;
}

export interface DigiPARTConnectionStatus {
  // Legacy API key-based connection (deprecated)
  endpoint?: string | null;
  endpoint_masked?: string;
  api_key_set?: boolean;
  connection_status?: 'untested' | 'success' | 'failed' | null;
  connection_tested_at?: string | null;
  test_result?: {
    connected: boolean;
    message: string;
    error?: string;
  };
  
  // Session-based authentication
  assigned?: boolean;
  authenticated?: boolean;
  assigned_username?: string;
  user_email?: string;
  organization_name?: string;
  organization_id?: string;
  expires_at?: string;
  status?: 'not_assigned' | 'assigned' | 'connected' | 'session_expired' | 'failed';
  message?: string;
}

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  prompt_text: string;
  estimated_tokens_per_row: number;
  is_default: boolean;
  is_favorite: boolean;
  usage_count: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface GlobalPromptTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  prompt_text: string;
  estimated_tokens_per_row: number;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface TokenUsage {
  id: string;
  date: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  cost_sgd: number;
  job_count: number;
  created_at: string;
  updated_at: string;
}

export interface TokenEstimation {
  prompt_text: string;
  row_count: number;
  estimated_tokens_per_row: number;
  estimated_tokens: {
    prompt_tokens: number;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
  estimated_cost: {
    usd: number;
    sgd: number;
  };
}

// Cost estimation response from API
export interface CostEstimation {
  filename: string;
  row_count: number;
  chunk_size: number;
  total_chunks: number;
  model_name: string;
  estimated_tokens: {
    grand_total_tokens: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_tokens_per_chunk: number;
    input_tokens_per_chunk: number;
    estimated_output_tokens_per_chunk: number;
  };
  estimated_cost: {
    usd: number;
    sgd: number;
    per_row_usd: number;
    per_chunk_usd: number;
    per_chunk_sgd?: number;
  };
  pricing_note?: string;
  is_free?: boolean;
}

// Resume job API response — backend returns different shapes by pipeline type:
//   Standard job:   { message, new_job: { id, ... }, original_job_id }
//   Dimension job:  { message, id, status, ... }  (job spread at root level)
export interface ResumeJobResponse {
  message: string;
  // Present for standard (non-dimension) resume — new job created
  new_job?: Pick<ProcessingJob, 'id' | 'status' | 'job_name'> & Record<string, unknown>;
  original_job_id?: number | string;
  // Present for dimension resume — same job re-queued in place
  id?: number | string;
  status?: ProcessingJob['status'];
}

// Generic paginated response from Django REST Framework
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// React Router location state
export interface LocationState {
  fileId?: string;
  from?: string;
  jobId?: number | string;
}

// Prompt template CRUD interfaces
export interface PromptTemplateCreate {
  name: string;
  description: string;
  prompt_text: string;
  estimated_tokens_per_row?: number;
  is_default?: boolean;
  is_favorite?: boolean;
}

export interface PromptTemplateUpdate extends Partial<PromptTemplateCreate> {}

// Data utility configuration
export interface DataUtilityConfig {
  columns?: string[];
  column_names?: Record<string, string>;
  operation?: string;
  parameters?: Record<string, unknown>;
}

// ─── DigiPART System Configuration ──────────────────────────────────────────

export type DigipartEnvironment = 'staging' | 'production' | 'custom';

export interface DigipartConfigResponse {
  environment: DigipartEnvironment;
  resolved_url: string;
  custom_url: string | null;
  active_sessions: number;
  last_changed_by: string | null;
  last_changed_at: string | null;
}

export interface DigipartConfigUpdateRequest {
  environment: DigipartEnvironment;
  custom_url?: string;
}

// Column analysis interfaces
export interface ColumnInfo {
  name: string;
  unique_count: number;
  null_count: number;
  uniqueness_ratio: number;
  is_suitable: boolean;
  sample_values: (string | number | boolean | null)[];
}

export interface ColumnAnalysisResult {
  file_id: string;
  filename: string;
  total_rows: number;
  total_columns: number;
  columns: ColumnInfo[];
  suggested_join_column: string | null;
  suggestion_reason: string;
}

// API Response wrapper interfaces
export interface ApiResponse<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
  data?: T;
}

// Error response from API
export interface ApiError {
  message?: string;
  detail?: string;
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

// Query parameters for list endpoints
export interface ListQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  status?: string;
  [key: string]: string | number | boolean | undefined;
}

// Template selection interface
export interface TemplateSelection {
  id: string;
  name: string;
  description?: string;
  prompt_text?: string;
  [key: string]: unknown;
}

// Update profile data
export interface ProfileUpdateData {
  full_name?: string;
  email?: string;
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
}

// Chart data types
export interface ChartEntry {
  status: string;
  count: number;
  name?: string;
  value?: number;
}

// File preview table row
export interface FilePreviewRow {
  [key: string]: string | number | boolean | null;
}

// Data utility result summary
export interface DataUtilityResultSummary {
  operation: string;
  rows_processed: number;
  columns_affected?: string[];
  errors?: Array<{
    row?: number;
    column?: string;
    message: string;
    details?: unknown;
  }>;
  [key: string]: unknown;
}

// DigiPART Part types
export interface DigiPARTPart {
  id: string;
  part_number: string;
  shape: 'TUBE' | 'CUBOID' | 'DN' | 'UNKNOWN' | string;
  description?: string;
  existing_dimensions_count: number;
  dimensions?: DigiPARTDimension[];
}

export interface DigiPARTCatalogue {
  id: string;
  name: string;
  description?: string;
  spare_parts_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface DigiPARTDimension {
  id: string;
  context: string;
  value: number;
  unit: string;
}

export interface DigiPARTDocument {
  id: string;
  filename: string;
  url?: string;
  content?: string;
}
